<script setup lang="ts">
import { useTemplateRef } from 'vue'
import type { BaseButtonEmits, BaseButtonProps } from './types'

const props = withDefaults(defineProps<BaseButtonProps>(), {
  is: 'button',
  busy: false,
  disabled: false,
  type: 'button',
})

const emit = defineEmits<BaseButtonEmits>()

const ref = useTemplateRef<HTMLButtonElement | HTMLAnchorElement>('ref')

defineExpose({
  focus() {
    ref.value?.focus()
  },
})

const handleClick = (event: MouseEvent) => {
  /**
   * In Safari, buttons are not focused automatically by the browser once pressed, instead they focus the nearest focusable ancestor.
   * To fix this, we need to manually focus the button after the user presses it.
   * But not in $nextTick, so other elements can take the focus if needed.
   */
  ref.value?.focus?.()

  if (props.busy || props.disabled) {
    event.preventDefault()
    return
  }

  emit('click', event)
}
</script>

<template>
  <component
    :is="is"
    ref="ref"
    :aria-busy="busy || undefined"
    :aria-disabled="disabled || undefined"
    :disabled="disabled || undefined"
    :type="is === 'button' ? type : undefined"
    @click="handleClick"
  >
    <slot />
  </component>
</template>
